import { useContext, useMemo } from 'react';
import { generatePath, matchPath, useLocation } from 'react-router-dom';
import { CleanUp, useAsyncEffect, useMemoOnce } from '@hilma/tools';

import { TransformObjectContext, LanguageContext, TranslatedI18nContext, TranslateContext, IsI18nRouterContext } from '../context';
import { Direction, I18n, I18nSelector, Language, Localize, Texts, TranslatedI18n } from '../types';
import { isLanguage } from '../functions';

export const createTranslatedI18nHook = <I extends I18n = I18n>() => {
	const useTranslatedI18n = () => useContext(TranslatedI18nContext)! as TranslatedI18n<I>;

	return useTranslatedI18n;
}

export const createTranslateHook = <I extends I18n = I18n>() => {
	const useTranslate = () => useContext(TranslateContext)! as (key: string | I18nSelector<I>) => string;

	return useTranslate;
}

export const createI18nHook = <I extends I18n = I18n>() => {
	const useTranslatedI18n = createTranslatedI18nHook<I>();

	function useI18n(): TranslatedI18n<I>;
	function useI18n<T>(selector: (i18n: TranslatedI18n<I>) => T): T;
	function useI18n(selector?: (i18n: TranslatedI18n<I>) => any) {
		const translatedI18n = useTranslatedI18n();
		const isSelector = useMemoOnce(() => !!selector);

		const i18n = useMemo(() => {
			return isSelector ? selector!(translatedI18n) : translatedI18n;
		}, [translatedI18n, isSelector]);

		return i18n;
	}

	return useI18n;
}

export const createTranslateTextsHook = <I extends I18n = I18n>() => {
	const useTranslate = createTranslateHook<I>();

	const useTranslateTexts = <T extends any[]>(texts: Texts<T, I>): T => {
		const translate = useTranslate();

		const translatedTexts = useMemo<T>(() => {
			return texts.map(translate) as T;
		}, [translate]);

		return translatedTexts;
	}

	return useTranslateTexts;
}

export const useBaseTranslatedI18n = createTranslatedI18nHook();
export const useBaseI18n = createI18nHook();
export const useBaseTranslate = createTranslateHook();
export const useBaseTranslateTexts = createTranslateTextsHook();

export const useLanguage = () => useContext(LanguageContext)!;
export const useTransformObject = () => useContext(TransformObjectContext)!;
export const useIsI18nRouter = () => useContext(IsI18nRouterContext)!;

export const useLanguageEffect = (effect: (language: Language) => (Promise<CleanUp> | CleanUp), deps: React.DependencyList = []) => {
	const language = useLanguage();

	useAsyncEffect(() => {
		return effect(language);
	}, [language, ...deps]);
}

export const usePathLanguage = () => {
	const location = useLocation();

	const pathLanguage = useMemo(() => {
		const match = matchPath('/:language/*', location.pathname);
		return isLanguage(match?.params.language) ? match?.params.language : null;
	}, [location.pathname]);

	return pathLanguage;
}

export const useDirection = () => {
	const language = useLanguage();

	const direction = useMemo<Direction>(() => {
		if (language === "en") return 'ltr';
		else if (language === "he") return 'rtl';
		else throw new Error("Language not valid");
	}, [language]);

	return direction;
}

export function useTransform<T>(object: Localize<T>): T;
export function useTransform<T>(object: Localize<T> | null): T | null;
export function useTransform<T>(object: Localize<T> | undefined): T | undefined;
export function useTransform<T>(object: Localize<T> | null | undefined): T | null | undefined;
export function useTransform<T>(objects: Localize<T>[]): T[];
export function useTransform<T>(objects: Localize<T>[] | null): T[] | null;
export function useTransform<T>(objects: Localize<T>[] | undefined): T[] | undefined;
export function useTransform<T>(objects: Localize<T>[] | null | undefined): T[] | null | undefined;
export function useTransform<T>(object: T | T[] | null | undefined) {
	const transformObject = useTransformObject();

	const transformed = useMemo(() => {
		if (!object) return object;

		if (Array.isArray(object)) return object.map(transformObject);

		return transformObject(object);
	}, [object, transformObject]);

	return transformed;
}

export const useNoLanguagePathname = (): string => {
	const location = useLocation();
	const language = useLanguage();

	const noLanguagePathname = useMemo<string>(() => {
		return location.pathname.split(`/${language}`)[1];
	}, [location.pathname, language]);

	return noLanguagePathname;
}

export const useSwitchedPath = () => {
	const language = useLanguage();

	const location = useLocation();

	const switchedPath = useMemo(() => {
		const [, , ...rest] = location.pathname.split('/');

		const newPath = generatePath(`/:langauge/${rest.join('/')}`, {
			langauge: language === Language.En ? Language.He : Language.En
		});

		return newPath;
	}, [language, location.pathname]);

	return switchedPath;
}